import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, btnDanger, overlayStyle, modalStyle, FormRow, fmtQty } from './InventoryShared';

const REASONS = ['damage', 'loss', 'breakage', 'expiry', 'theft', 'internal_use', 'found_stock', 'count_variance', 'data_correction', 'other'];

export default function InventoryAdjustments() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/inventory/adjustments?per_page=50`;
      if (status) url += `&status=${status}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status]);

  const fetchAux = useCallback(async () => {
    const [wRes, pRes] = await Promise.all([
      fetch(`${API}/companies/${companyId}/inventory/warehouses`, { headers: getAuthHeaders() }),
      fetch(`${API}/companies/${companyId}/inventory/products?per_page=200`, { headers: getAuthHeaders() }),
    ]);
    setWarehouses(await wRes.json());
    const pJson = await pRes.json();
    setProducts(Array.isArray(pJson.data) ? pJson.data : []);
  }, [companyId]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchAux(); }, [fetchAux]);

  const openView = async (id) => {
    const res = await fetch(`${API}/companies/${companyId}/inventory/adjustments/${id}`, { headers: getAuthHeaders() });
    setViewing(await res.json());
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>⚖️ Stock Adjustments</h2>
        <button style={btnPrimary} onClick={() => setCreating(true)}>+ New Adjustment</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <select style={{ ...inputStyle, width: 200 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="posted">Posted</option>
          <option value="rejected">Rejected</option>
          <option value="reversed">Reversed</option>
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Adjustment #', 'Warehouse', 'Reason', 'Lines', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No adjustments</td></tr>}
              {list.map(a => (
                <tr key={a.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                    <a href="#v" onClick={e => { e.preventDefault(); openView(a.id); }} style={{ color: '#3b82f6' }}>{a.adjustment_number}</a>
                  </td>
                  <td style={tdStyle}>{a.warehouse?.name}</td>
                  <td style={tdStyle}>{(a.reason || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{a.lines_count ?? 0}</td>
                  <td style={tdStyle}><Badge value={a.status} /></td>
                  <td style={tdStyle}><button style={btnSmall} onClick={() => openView(a.id)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <CreateAdjustmentModal companyId={companyId} warehouses={warehouses} products={products}
          onClose={() => setCreating(false)} onSaved={() => { setCreating(false); fetchList(); }} />
      )}
      {viewing && (
        <AdjustmentDetail companyId={companyId} adjustment={viewing}
          onClose={() => { setViewing(null); fetchList(); }} onRefresh={() => openView(viewing.id)} />
      )}
    </div>
  );
}

function CreateAdjustmentModal({ companyId, warehouses, products, onClose, onSaved }) {
  const [warehouseId, setWarehouseId] = useState('');
  const [reason, setReason] = useState('damage');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([{ product_id: '', quantity_delta: '', unit_cost: '' }]);
  const [saving, setSaving] = useState(false);

  const addLine = () => setLines([...lines, { product_id: '', quantity_delta: '', unit_cost: '' }]);
  const updateLine = (i, field, val) => setLines(lines.map((l, idx) => idx === i ? { ...l, [field]: val } : l));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!warehouseId) { alert('Warehouse is required'); return; }
    const validLines = lines.filter(l => l.product_id && l.quantity_delta !== '');
    if (validLines.length === 0) { alert('At least one line is required'); return; }
    setSaving(true);
    const res = await fetch(`${API}/companies/${companyId}/inventory/adjustments`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ warehouse_id: warehouseId, reason, notes, lines: validLines }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
    onSaved();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 640 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Adjustment</h3>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><FormRow label="Warehouse *">
            <select style={inputStyle} value={warehouseId} onChange={e => setWarehouseId(e.target.value)}>
              <option value="">— Select —</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Reason *">
            <select style={inputStyle} value={reason} onChange={e => setReason(e.target.value)}>
              {REASONS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow></div>
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', margin: '10px 0 6px' }}>LINES (signed qty: + increase, − decrease)</div>
        {lines.map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <select style={{ ...inputStyle, flex: 2 }} value={l.product_id} onChange={e => updateLine(i, 'product_id', e.target.value)}>
              <option value="">— Product —</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input type="number" placeholder="Qty Δ" style={{ ...inputStyle, flex: 1 }} value={l.quantity_delta} onChange={e => updateLine(i, 'quantity_delta', e.target.value)} />
            <input type="number" placeholder="Unit cost" style={{ ...inputStyle, flex: 1 }} value={l.unit_cost} onChange={e => updateLine(i, 'unit_cost', e.target.value)} />
            <button style={btnSmall} onClick={() => removeLine(i)}>✕</button>
          </div>
        ))}
        <button style={btnSecondary} onClick={addLine}>+ Add Line</button>

        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 50, marginTop: 10 }} value={notes} onChange={e => setNotes(e.target.value)} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Create Draft'}</button>
        </div>
      </div>
    </div>
  );
}

function AdjustmentDetail({ companyId, adjustment, onClose, onRefresh }) {
  const [busy, setBusy] = useState(false);

  const act = async (action) => {
    if (action === 'reverse' && !window.confirm('Reverse this posted adjustment?')) return;
    setBusy(true);
    const res = await fetch(`${API}/companies/${companyId}/inventory/adjustments/${adjustment.id}/${action}`, { method: 'POST', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Action failed'); }
    setBusy(false); onRefresh();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 640 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{adjustment.adjustment_number} <Badge value={adjustment.status} /></h3>
        <div style={{ marginBottom: 10 }}><b>Warehouse:</b> {adjustment.warehouse?.name} &nbsp; <b>Reason:</b> {(adjustment.reason || '').replace(/_/g, ' ')}</div>
        <table style={tableStyle}>
          <thead><tr>{['Product', 'Qty Δ', 'Unit Cost'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {(adjustment.lines || []).map(l => (
              <tr key={l.id}>
                <td style={tdStyle}>{l.product?.name || l.product_id}</td>
                <td style={tdStyle}>{l.quantity_delta > 0 ? '+' : ''}{fmtQty(l.quantity_delta)}</td>
                <td style={tdStyle}>{l.unit_cost}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          {adjustment.status === 'draft' && <button style={btnSecondary} onClick={() => act('submit')} disabled={busy}>Submit for Approval</button>}
          {adjustment.status === 'draft' && <button style={btnPrimary} onClick={() => act('post')} disabled={busy}>Post Directly</button>}
          {adjustment.status === 'pending_approval' && <button style={btnPrimary} onClick={() => act('approve')} disabled={busy}>Approve</button>}
          {adjustment.status === 'pending_approval' && <button style={btnDanger} onClick={() => act('reject')} disabled={busy}>Reject</button>}
          {adjustment.status === 'approved' && <button style={btnPrimary} onClick={() => act('post')} disabled={busy}>Post</button>}
          {adjustment.status === 'posted' && <button style={btnDanger} onClick={() => act('reverse')} disabled={busy}>Reverse</button>}
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
